import { useState, useEffect } from 'react';
import { cardsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import {
  CreditCard, Lock, Unlock, Award, Plane, Settings, Link2, Unlink,
  Globe, ShoppingCart, Banknote, Bell, DollarSign, Building2
} from 'lucide-react';
import { toast } from 'sonner';

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Link card form
  const [linkBankName, setLinkBankName] = useState('');
  const [linkAccountNumber, setLinkAccountNumber] = useState('');
  const [linkRoutingNumber, setLinkRoutingNumber] = useState('');
  const [linkAccountHolderName, setLinkAccountHolderName] = useState('');
  const [linkAccountType, setLinkAccountType] = useState('checking');
  const [linkLoading, setLinkLoading] = useState(false);

  // Card settings form
  const [spendingLimit, setSpendingLimit] = useState('');
  const [internationalEnabled, setInternationalEnabled] = useState(true);
  const [onlineShoppingEnabled, setOnlineShoppingEnabled] = useState(true);
  const [atmWithdrawalEnabled, setAtmWithdrawalEnabled] = useState(true);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      const res = await cardsAPI.list();
      setCards(res.data.cards);
    } catch (err) {
      toast.error('Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  const handleFreeze = async (cardId, currentStatus) => {
    try {
      await cardsAPI.freeze(cardId);
      toast.success(`Card ${currentStatus ? 'unfrozen' : 'frozen'} successfully`);
      loadCards();
    } catch (err) {
      toast.error('Failed to update card status');
    }
  };

  const openLinkDialog = (card) => {
    setSelectedCard(card);
    setLinkBankName('');
    setLinkAccountNumber('');
    setLinkRoutingNumber('');
    setLinkAccountHolderName('');
    setLinkAccountType('checking');
    setLinkDialogOpen(true);
  };

  const handleLinkCard = async (e) => {
    e.preventDefault();
    if (!linkBankName || !linkAccountNumber || !linkRoutingNumber || !linkAccountHolderName) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLinkLoading(true);
    try {
      await cardsAPI.linkExternal({
        card_id: selectedCard._id,
        bank_name: linkBankName,
        account_number: linkAccountNumber,
        routing_number: linkRoutingNumber,
        account_holder_name: linkAccountHolderName,
        account_type: linkAccountType
      });
      toast.success(`Card linked to ${linkBankName} successfully!`);
      setLinkDialogOpen(false);
      loadCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link card');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkCard = async (cardId, bankName) => {
    try {
      await cardsAPI.unlinkExternal(cardId, bankName);
      toast.success(`Card unlinked from ${bankName}`);
      loadCards();
    } catch (err) {
      toast.error('Failed to unlink card');
    }
  };

  const openSettingsDialog = (card) => {
    setSelectedCard(card);
    setSpendingLimit(card.spending_limit ? String(card.spending_limit) : '');
    setInternationalEnabled(card.international_enabled !== false);
    setOnlineShoppingEnabled(card.online_shopping_enabled !== false);
    setAtmWithdrawalEnabled(card.atm_withdrawal_enabled !== false);
    setNotificationEnabled(card.notification_enabled !== false);
    setSettingsDialogOpen(true);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    try {
      await cardsAPI.updateSettings(selectedCard._id, {
        spending_limit: spendingLimit ? parseFloat(spendingLimit) : null,
        international_enabled: internationalEnabled,
        online_shopping_enabled: onlineShoppingEnabled,
        atm_withdrawal_enabled: atmWithdrawalEnabled,
        notification_enabled: notificationEnabled
      });
      toast.success('Card settings updated!');
      setSettingsDialogOpen(false);
      loadCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const getCardBrand = (type) => {
    if (type === 'visa') return 'VISA';
    if (type === 'mastercard') return 'MASTERCARD';
    return 'AMEX';
  };

  if (loading) {
    return <div className="page-container"><p>Loading cards...</p></div>;
  }

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Card Management</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {cards.map(card => (
          <div key={card._id}>
            {/* Card Visual */}
            <div className={`credit-card ${card.type} mb-4`} data-testid={`credit-card-${card.type === 'visa' ? 'visa-platinum' : card.type === 'mastercard' ? 'mastercard-cash-rewards' : 'amex-unlimited'}`}>
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/70">{card.name}</p>
                  <Badge className={`mt-1 text-[10px] ${card.is_frozen ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                    {card.is_frozen ? 'Frozen' : 'Active'}
                  </Badge>
                </div>
                <p className="text-sm font-bold text-white/80">{getCardBrand(card.type)}</p>
              </div>
              <div className="relative z-10">
                <p className="font-mono text-lg tracking-widest text-white/85 mt-6">
                  **** **** **** {card.last_four}
                </p>
                <p className="mt-4 text-base font-semibold tracking-wider" data-testid="credit-card-cardholder-name">
                  {card.cardholder_name}
                </p>
              </div>
              <div className="flex items-end justify-between mt-4 relative z-10">
                <div>
                  <p className="text-xs text-white/60">Credit Limit</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {card.credit_limit >= 999999999 ? 'Unlimited' : formatCurrency(card.credit_limit)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/60">Expires</p>
                  <p className="text-sm font-medium">{card.expiry}</p>
                </div>
              </div>
            </div>

            {/* Card Controls */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Available Credit</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {card.available_credit >= 999999999 ? 'Unlimited' : formatCurrency(card.available_credit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Balance</p>
                    <p className="text-lg font-semibold tabular-nums">{formatCurrency(card.current_balance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">APR</p>
                    <p className="text-sm font-medium">{card.apr}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Rewards Points</p>
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Award className="h-3 w-3 text-yellow-500" />
                      {card.rewards_points?.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Card Status Controls */}
                <Separator className="my-3" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {card.is_frozen ? <Lock className="h-4 w-4 text-red-500" /> : <Unlock className="h-4 w-4 text-green-500" />}
                      <span className="text-sm font-medium">{card.is_frozen ? 'Card Frozen' : 'Card Active'}</span>
                    </div>
                    <Switch
                      data-testid="card-freeze-switch"
                      checked={card.is_frozen}
                      onCheckedChange={() => handleFreeze(card._id, card.is_frozen)}
                    />
                  </div>

                  {/* Feature Toggles */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">International</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {card.international_enabled !== false ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Online Shopping</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {card.online_shopping_enabled !== false ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  {card.spending_limit && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-orange-500" />
                        <span className="text-sm">Daily Spending Limit</span>
                      </div>
                      <span className="text-sm font-medium tabular-nums">{formatCurrency(card.spending_limit)}</span>
                    </div>
                  )}
                </div>

                {/* Linked External Accounts */}
                {card.linked_accounts && card.linked_accounts.length > 0 && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">LINKED EXTERNAL ACCOUNTS</p>
                      {card.linked_accounts.map((la, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 mb-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-[#0B5CAB]" />
                            <div>
                              <p className="text-xs font-medium">{la.bank_name}</p>
                              <p className="text-[11px] text-muted-foreground">{la.account_number_masked} &middot; {la.account_type}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-red-600 hover:text-red-700"
                            onClick={() => handleUnlinkCard(card._id, la.bank_name)}
                          >
                            <Unlink className="h-3 w-3 mr-1" /> Unlink
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <Separator className="my-3" />

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline" size="sm" className="text-xs"
                    onClick={() => openSettingsDialog(card)}
                    data-testid="card-settings-button"
                  >
                    <Settings className="h-3 w-3 mr-1" /> Settings
                  </Button>
                  <Button
                    variant="outline" size="sm" className="text-xs"
                    onClick={() => openLinkDialog(card)}
                    data-testid="card-link-external-button"
                  >
                    <Link2 className="h-3 w-3 mr-1" /> Link Bank
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" data-testid="card-travel-notice-button">
                    <Plane className="h-3 w-3 mr-1" /> Travel Notice
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" data-testid="card-replacement-button">
                    <CreditCard className="h-3 w-3 mr-1" /> Replace Card
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Link Card to External Bank Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              Link {selectedCard?.name} to External Bank
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLinkCard} className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-xs text-blue-600 font-medium">Card</p>
              <p className="text-sm font-semibold">{selectedCard?.name} (**** {selectedCard?.last_four})</p>
            </div>
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input data-testid="link-bank-name-input" placeholder="Chase, Wells Fargo, etc." value={linkBankName} onChange={e => setLinkBankName(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Account Holder Name</Label>
              <Input data-testid="link-holder-name-input" placeholder="Full legal name" value={linkAccountHolderName} onChange={e => setLinkAccountHolderName(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input data-testid="link-account-number-input" placeholder="123456789" value={linkAccountNumber} onChange={e => setLinkAccountNumber(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Routing Number</Label>
                <Input data-testid="link-routing-number-input" placeholder="021000021" value={linkRoutingNumber} onChange={e => setLinkRoutingNumber(e.target.value)} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select value={linkAccountType} onValueChange={setLinkAccountType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
              <Button data-testid="link-card-submit-button" type="submit" disabled={linkLoading}>
                {linkLoading ? 'Linking...' : 'Link Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Card Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              {selectedCard?.name} Settings
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div className="space-y-2">
              <Label>Daily Spending Limit (USD)</Label>
              <Input
                data-testid="card-spending-limit-input"
                type="number" step="0.01" placeholder="No limit"
                value={spendingLimit} onChange={e => setSpendingLimit(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Leave empty for no limit</p>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">International Transactions</span>
                </div>
                <Switch checked={internationalEnabled} onCheckedChange={setInternationalEnabled} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Online Shopping</span>
                </div>
                <Switch checked={onlineShoppingEnabled} onCheckedChange={setOnlineShoppingEnabled} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-orange-500" />
                  <span className="text-sm">ATM Withdrawals</span>
                </div>
                <Switch checked={atmWithdrawalEnabled} onCheckedChange={setAtmWithdrawalEnabled} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">Transaction Notifications</span>
                </div>
                <Switch checked={notificationEnabled} onCheckedChange={setNotificationEnabled} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
              <Button data-testid="card-save-settings-button" type="submit" disabled={settingsLoading}>
                {settingsLoading ? 'Saving...' : 'Save Settings'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
